import React, { useMemo, useState, useCallback, useRef } from 'react';
import { formatDateTime } from '../../utils/formatters';
import { detectAxisBreak, makeBreakScale, formatAxisValue, axisTicks, axisDecimals, formatTick, niceMax, niceTicks } from '../../utils/chartYScale';
import { listenDrag } from '../../utils/dragListen';
import './PanelChart.css';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, BarChart, Bar,
  ComposedChart, Area, ReferenceArea,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  // recharts 3.8 의 차트 컨텍스트 훅 — 내부 척도에 공식적으로 닿는 유일한 길이다.
  // (recharts 2 의 <Customized> props.xAxisMap 은 3.x 에서 사라졌다)
  useXAxisScale, useYAxisScale, useActiveTooltipLabel, useIsTooltipActive, usePlotArea,
} from 'recharts';

// 색상 팔레트 (yFieldsConfig 에 명시 색상이 없는 yField 의 fallback)
const COLORS_LIGHT = ['#3b82f6', '#10b981', '#f97316', '#a855f7', '#ef4444', '#0ea5e9'];
const COLORS_DARK  = ['#60a5fa', '#34d399', '#fb923c', '#c084fc', '#f87171', '#38bdf8'];

// =====================================================================
// 호버 값 라벨 — 툴팁 박스 대신 **그림 안에 직접** 쓴다.
//
// 왜: 불투명 박스가 그래프를 가려 정작 보려던 구간을 못 보게 된다(사용자 신고).
// 값은 그 범주가 있는 자리에 있어야 눈이 짝짓는 데 힘이 안 든다.
//
// 구현은 recharts 의 <Customized> 를 쓴다 — 내부 척도(xAxisMap/yAxisMap)에 닿아야
// 쌓인 띠의 **위아래 핑셀**을 정확히 알 수 있다. <LabelList> 는 점마다 그리는 데다
// 쌓인 띠의 밑변을 알려 주지 않는다.
//
// 배경 박스를 두지 않고 **글자 테두리를 배경색으로 두껍게 깔아** 가독성만 확보한다
// (paint-order: stroke). 어느 색 위에서든 읽히고, 그래프는 하나도 가리지 않는다.
// =====================================================================

/**
 * 끊긴 축 표시 — **표시 없는 끊긴 축은 거짓말이다.**
 *
 * 접은 자리에 물결(≈)을 그어 "여기서 눈금이 건너뛴다" 를 말한다. 이것이 없으면
 * 기울기가 실제보다 완만해 보이는데 아무도 그 사실을 모른다.
 */
function AxisBreakMark({ displayValue, color, bgColor, fontSize }) {
  const yScale = useYAxisScale('left');
  const plot = usePlotArea();
  if (!yScale || !plot) return null;
  const y = yScale(displayValue);
  if (y == null || Number.isNaN(y)) return null;
  const x0 = plot.x;
  const x1 = plot.x + plot.width;
  return (
    <g pointerEvents="none" className="panelchart-axis-break">
      {/* 접은 자리를 배경색으로 덮어 선이 지나가지 않는 것처럼 보이게 한다 */}
      <rect x={x0} y={y - 4} width={x1 - x0} height={8} fill={bgColor} opacity={0.92} />
      <line x1={x0} y1={y - 3} x2={x1} y2={y - 1} stroke={color} strokeWidth={1} opacity={0.55} />
      <line x1={x0} y1={y + 1} x2={x1} y2={y + 3} stroke={color} strokeWidth={1} opacity={0.55} />
      <text
        x={x0 - 4}
        y={y}
        textAnchor="end"
        dominantBaseline="middle"
        fill={color}
        style={{ fontSize, opacity: 0.8 }}
      >
        {'\u2248'}
      </text>
    </g>
  );
}

/**
 * 호버 라벨의 세로 자리 — 겹치지 않게 lineH 간격으로 벌리고, [top, bottom] 안에 들인다(2026-10-22).
 * 위에서부터 아래로 밀어 겹침을 풀고 → 아래로 넘친 만큼 통째로 올리고 → 그래도 위로 넘치면 위끝부터 다시 내린다(칸이 모자라면 아래로 넘친다).
 * @param {Array<{y:number}>} items  y 를 고친다(순서는 y 오름차순으로 바뀐다)
 */
export function spreadLabels(items, top, bottom, lineH) {
  items.sort((a, b) => a.y - b.y);
  for (let i = 1; i < items.length; i++) {
    if (items[i].y - items[i - 1].y < lineH) items[i].y = items[i - 1].y + lineH;
  }
  const last = items[items.length - 1];
  if (last && last.y > bottom) {
    const shift = last.y - bottom;
    items.forEach((it) => { it.y -= shift; });
  }
  if (items[0] && items[0].y < top) {
    items[0].y = top;
    for (let i = 1; i < items.length; i++) {
      if (items[i].y - items[i - 1].y < lineH) items[i].y = items[i - 1].y + lineH;
    }
  }
  return items;
}

/** 툴팁을 달아 두되 아무것도 그리지 않는다 — activeTooltipIndex 만 얻으려는 것이다 */
const renderNoTooltip = () => null;

function pickScale(map, key) {
  if (!map) return null;
  const entry = map[key] != null ? map[key] : map[Object.keys(map)[0]];
  return entry || null;
}

/** 카테고리(band) 척도면 칸 가운데로 밀어 준다 */
function scaleX(axis, value) {
  if (!axis || !axis.scale) return null;
  const x = axis.scale(value);
  if (x == null || Number.isNaN(x)) return null;
  const bw = typeof axis.scale.bandwidth === 'function' ? axis.scale.bandwidth() : 0;
  return x + bw / 2;
}

/**
 * 차트 **안에** 두면 recharts 컨텍스트 훅을 쓸 수 있다.
 *
 * @param {object} props
 * @param {'stack'|'line'} props.mode
 * @param {Array} props.data          차트에 넘긴 그대로의 행
 * @param {string} props.xDataKey
 * @param {string[]} props.fields     그릴 계열(순서는 쌓는 순서와 같다)
 */
function HoverValueLabels({
  mode, data, xDataKey, fields, fieldAxis,
  seriesName, colorOf, yFields, bgColor, fontSize, valueSuffix,
  // 끊긴 축이면 데이터가 표시 좌표로 옮겨져 있다 — 사람에게는 **원래 값**을 보여야 한다
  inverse,
}) {
  const active = useIsTooltipActive();
  const label = useActiveTooltipLabel();
  const xScale = useXAxisScale();
  const yLeft = useYAxisScale('left');
  const yRight = useYAxisScale('right');
  const plot = usePlotArea();

  if (!active || label == null || !Array.isArray(data) || !xScale) return null;
  // activeLabel 은 x 값이다 — 그 값을 가진 행을 찾는다(인덱스가 아니라 값이라
  // 정렬이 바뀜어도 안전하다).
  const row = data.find(r => r && r[xDataKey] === label);
  if (!row) return null;
  const cx = xScale(label);
  if (cx == null || Number.isNaN(cx)) return null;

  const halo = {
    paintOrder: 'stroke',
    stroke: bgColor,
    strokeWidth: 3.5,
    strokeLinejoin: 'round',
    fontSize,
    fontWeight: 600,
  };
  const out = [];

  // 선 그래프 — 라벨은 점의 **위쪽**에 두고 점에서 45° 사선 → 짧은 수평선 → 글자로 잇는다(2026-10-16 요청). 점에 바로 붙이면 선과 글이 겹쳐 읽기 어렵다.
  // 라벨끼리 붙으면 위아래로 밀어 겹침을 푼다(밀린 만큼 사선이 길어진다 — 늘 45°)
  // 쌓은 영역도 같은 모양이다(2026-10-22 요청) — 예전에는 띠 **안에** 글자를 넣어서 얇은 띠는 값이 안 보였고, 값을 다듬지 않아
  // 「기타 1290.7350000000004」 처럼 부동소수 꼬리가 그대로 나왔다. 점은 각 띠의 **가운데 높이**, 값은 그 띠 자신의 값(쌓인 합이 아니다).
  // 띠의 위끝에 두면 쌓인 그래프는 위끝이 모두 위쪽에 몰려, 겹침을 풀면서 라벨이 그림 위로 밀려 나가 잘렸다(실측)
  const LIFT = 12;          // 기본 높이(= 사선의 가로·세로 길이)
  const TAIL = 10;          // 사선 끝의 수평선 길이
  const placed = [];
  if (mode === 'stack') {
    if (!yLeft) return null;
    let acc = 0;
    fields.forEach((f) => {
      const v = Number(row[f]) || 0;
      acc += v;
      if (v <= 0) return;
      const yTop = yLeft(acc);
      const yBottom = yLeft(acc - v);
      if (yTop == null || Number.isNaN(yTop) || yBottom == null || Number.isNaN(yBottom)) return;
      const yMid = (yTop + yBottom) / 2;
      out.push(<circle key={`dot-${f}`} cx={cx} cy={yMid} r={3.5} fill={colorOf(f, yFields.indexOf(f))} stroke={bgColor} strokeWidth={1.5} />);
      placed.push({ f, y0: yMid, y: yMid, v });
    });
  } else {
    fields.forEach((f) => {
      const raw = row[f];
      if (raw == null || Number.isNaN(Number(raw))) return;
      const scale = (fieldAxis && fieldAxis[f] === 'right') ? yRight : yLeft;
      if (!scale) return;
      const y0 = scale(Number(raw));
      if (y0 == null || Number.isNaN(y0)) return;
      placed.push({ f, y0, y: y0 - LIFT, v: inverse ? inverse(Number(raw)) : Number(raw) });
    });
  }
  // 겹침을 풀되 **그림 영역 안에서** — 위로만 밀면 위쪽에 몰린 라벨이 그림 밖으로 나가 잘리고, 아래로만 밀면 가로축 글자 위로 내려간다
  const lineH = fontSize + 3;
  if (plot) spreadLabels(placed, plot.y + lineH / 2, plot.y + plot.height - lineH / 2, lineH);
  // 오른쪽 끝 가까이면 라벨을 점의 **왼쪽**에 둔다 — 오른쪽에 두면 그림 영역 밖으로 나가 잘린다(2026-10-14 신고). 글 폭은 글자 수로 어림한다
  const plotRight = plot ? plot.x + plot.width : Infinity;
  const textW = (txt) => String(txt).length * fontSize * 0.62;
  placed.forEach(({ f, y0, y, v }) => {
    const txt = `${seriesName(f)} ${formatAxisValue(v)}${valueSuffix || ''}`;
    // 밀려서 점 아래로 내려간 라벨은 사선도 아래로. 너무 가까우면 최소 길이를 둔다(사선이 안 보인다)
    let dy = y - y0;
    if (Math.abs(dy) < 6) dy = dy > 0 ? 6 : -6;
    const d = Math.abs(dy);
    const flip = cx + d + TAIL + 3 + textW(txt) > plotRight;
    const dir = flip ? -1 : 1;
    const x1 = cx + dir * d;
    const x2 = x1 + dir * TAIL;
    const ly = y0 + dy;
    const color = colorOf(f, yFields.indexOf(f));
    out.push(
      <path key={`lead-${f}`} d={`M${cx},${y0} L${x1},${ly} L${x2},${ly}`} fill="none" stroke={color} strokeWidth={1} opacity={0.85} />
    );
    out.push(
      <text
        key={f}
        x={x2 + dir * 3}
        y={ly}
        textAnchor={flip ? 'end' : 'start'}
        dominantBaseline="middle"
        fill={color}
        style={halo}
      >
        {txt}
      </text>
    );
  });
  return <g pointerEvents="none" className="panelchart-hover-labels">{out}</g>;
}

// =====================================================================
// 시간축 동기화 (displayMode='sync') helper —
//   timeWindowMin 만으로 tick 간격·라벨 포맷·tick 위치 결정.
//   여러 패널이 같은 timeWindowMin 을 가지면 자동으로 X 축이 정렬됨.
// auto 모드(default) 에서는 사용 안 됨 — makeTimeTickFormatter 폴백.
// =====================================================================
function deriveTimeAxisConfig(timeWindowMin) {
  const m = timeWindowMin;
  // [상한 분, tickIntervalMs, format string]
  // format: 'HMS'(HH:MM:SS) / 'HM'(HH:MM) / 'MD_HM'(M/D HH:MM) / 'MD'(M/D)
  const table = [
    [5,      30 * 1000,             'HMS'  ],
    [15,     60 * 1000,             'HM'   ],
    [30,     5 * 60 * 1000,         'HM'   ],
    [60,     5 * 60 * 1000,         'HM'   ],
    [180,    15 * 60 * 1000,        'HM'   ],
    [360,    30 * 60 * 1000,        'HM'   ],
    [720,    60 * 60 * 1000,        'HM'   ],
    [1440,   2 * 60 * 60 * 1000,    'MD_HM'],
    [4320,   6 * 60 * 60 * 1000,    'MD_HM'],
    [10080, 12 * 60 * 60 * 1000,    'MD_HM'],
  ];
  for (const [maxMin, intervalMs, fmt] of table) {
    if (m <= maxMin) return { intervalMs, fmt };
  }
  return { intervalMs: 24 * 60 * 60 * 1000, fmt: 'MD' };
}

function formatTimeTick(ms, fmt) {
  const d = new Date(ms);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  const HM  = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (fmt === 'HMS')   return `${HM}:${pad(d.getSeconds())}`;
  if (fmt === 'MD_HM') return `${d.getMonth() + 1}/${d.getDate()} ${HM}`;
  if (fmt === 'MD')    return `${d.getMonth() + 1}/${d.getDate()}`;
  return HM;  // 'HM' default
}

function generateTimeTicks(domain, intervalMs) {
  if (!Array.isArray(domain) || !intervalMs) return undefined;
  const [start, end] = domain;
  if (!(end > start)) return undefined;
  // 시작점을 interval 경계로 snap (예: 5min 간격이면 :00, :05, :10, ...)
  const first = Math.ceil(start / intervalMs) * intervalMs;
  const ticks = [];
  for (let t = first; t <= end; t += intervalMs) {
    ticks.push(t);
    if (ticks.length > 500) break;   // 안전 가드
  }
  return ticks;
}

// =====================================================================
// chartType='job' (시계열 Gantt) helper —
//   row → 시작/종료 ms, 진행률, 평균처리량 계산.
//   단위 변환은 안 함 — SQL 에서 적절한 단위로 SELECT 하면 됨.
// =====================================================================
function jobParseTime(v) {
  if (v == null || v === '') return NaN;
  if (typeof v === 'number') return v;
  return new Date(v).getTime();
}
function jobNumOrNull(v) {
  if (v == null || v === '') return null;
  const n = (typeof v === 'number') ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}
function jobFormatRate(rate) {
  if (rate == null || !isFinite(rate) || rate <= 0) return null;
  if (rate < 1)   return `${rate.toFixed(2)}/s`;
  if (rate < 100) return `${rate.toFixed(1)}/s`;
  return `${Math.round(rate).toLocaleString()}/s`;
}

// 시간축 tick formatter 팩토리 — data 의 시간 범위를 보고 날짜 포함 여부 결정.
// 24시간 이상 범위면 "M/D H:MM" 표기, 그 미만이면 "H:MM". 문자열 x 축은 그대로 반환.
// auto 모드(default) 에서 사용. sync 모드는 위 deriveTimeAxisConfig 사용.
function makeTimeTickFormatter(data, xField) {
  const showDate = (() => {
    if (!Array.isArray(data) || data.length < 2) return false;
    const first = new Date(data[0]?.[xField]);
    const last = new Date(data[data.length - 1]?.[xField]);
    if (isNaN(first.getTime()) || isNaN(last.getTime())) return false;
    return Math.abs(last - first) >= 24 * 60 * 60 * 1000;
  })();
  return (value) => {
    if (value === null || value === undefined || value === '') return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const hm = `${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')}`;
    if (!showDate) return hm;
    return `${d.getMonth() + 1}/${d.getDate()} ${hm}`;
  };
}

/**
 * panel.yAxis / panel.yAxisRight 의 옵션 적용 후 ticks/domain/tickFormatter 계산.
 * - tickStep 이 지정되면 0..ceil(max/step)*step 까지 step 간격 ticks 생성
 * - showUnit + unit 지정되면 tick 라벨에 단위 suffix 추가
 */
function buildAxisProps(opts, data, fieldsOnAxis, side = 'left', textColor = '#374151', labelFontSize = 12, stacked = false) {
  const o = opts || {};
  const result = {
    label: undefined,
    ticks: undefined,
    domain: undefined,
    tickFormatter: undefined,
    allowDataOverflow: undefined,
  };

  // 축 제목 (label) — 좌/우 축에 따라 회전 방향과 위치 다름.
  // 좌축: insideLeft + 시계반대 -90°. 라벨이 tick numbers 왼쪽, 차트 좌측 라인은 tick 의 우측.
  //   (recharts 레이아웃 상 라벨-차트 라인 거리는 tick number 폭만큼 떨어지는 게 표준)
  // 우축: insideRight + 시계방향 90°.
  // dx 로 살짝 안쪽(차트 라인 방향) 으로 끌어와 거리 단축.
  const title = o.title;
  const showTitle = o.showTitle !== false;  // 기본 true
  // 축 이름은 **축 위쪽에 가로로 짧게** 둔다(2026-10-16) — 세로로 눈금 숫자 옆에 두던 때 숫자와 겹쳐(「0.55」 처럼 자릿수가 늘면 더)
  // 그래프마다 이름을 빼야 했다. 위쪽 여백에 두면 눈금 폭과 무관하다. 짧은 이름(shortTitle)이 있으면 그것, 없으면 앞 14자 + 「…」, 전체는 툴팁(<title>)
  if (title && showTitle) {
    const isRight = side === 'right';
    const full = String(title);
    const short = String(o.shortTitle || (full.length > 14 ? `${full.slice(0, 13)}…` : full));
    result.topLabel = true;
    result.label = {
      position: 'top',
      // 내용을 직접 그린다 — 왼쪽 축은 축 왼쪽 끝에서 오른쪽으로, 오른쪽 축은 오른쪽 끝에서 왼쪽으로 뻗어 그림 위 여백에 놓인다(잘리지 않게)
      content: ({ viewBox }) => {
        if (!viewBox) return null;
        const x = isRight ? viewBox.x + viewBox.width - 2 : viewBox.x + 2;
        return (
          <text x={x} y={viewBox.y - 11} textAnchor={isRight ? 'end' : 'start'} fontSize={Math.max(10, labelFontSize - 1)} fill={textColor} className="panelchart-ytitle">
            <title>{full}</title>
            {short}
          </text>
        );
      },
    };
  }

  // 단위 표시(눈금 글자 뒤) · 천 단위 구분(1,234,567 — 블록 수처럼 자릿수가 큰 값, 2026-10-14). 글자는 맨 끝에서 만든다
  const unit = o.unit;
  const showUnit = o.showUnit !== false;

  // tickStep — explicit ticks 생성
  if (o.tickStep && o.tickStep > 0 && data && data.length > 0 && fieldsOnAxis.length > 0) {
    let max = 0;
    for (const row of data) {
      for (const f of fieldsOnAxis) {
        const v = parseFloat(row[f]);
        if (!isNaN(v) && v > max) max = v;
      }
    }
    if (max > 0) {
      const top = Math.ceil(max / o.tickStep) * o.tickStep;
      const ticks = [];
      // 너무 많은 tick 방지 (max 50개)
      const cap = 50;
      let v = 0;
      while (v <= top && ticks.length < cap) {
        ticks.push(v);
        v += o.tickStep;
      }
      result.ticks = ticks;
      result.domain = [0, top];
    }
  }

  // 명시 범위 — tickStep 으로 유도된 domain 보다 우선한다.
  // 비율축(0~100%)처럼 데이터와 무관하게 눈금이 고정돼야 하는 축에 쓴다.
  // (데이터 최대값으로 domain 을 잡으면 같은 40% 가 어떤 때는 축 끝, 어떤 때는 중간에 찍혀
  //  높이만 보고 비율을 읽을 수 없다.)
  if (o.min != null && o.max != null) {
    result.domain = [o.min, o.max];
    // **명시 범위는 명시 그대로다.** 이것이 없으면 데이터가 반올림 오차로 100.1 만 돼도
    // recharts 가 축을 늘려 "100% 스택인데 y축 최댓값이 105" 가 된다(실제 신고).
    result.allowDataOverflow = true;
  } else if (o.min != null || o.max != null) {
    // 한쪽만 정했으면 다른 쪽은 「보기 좋은」 끝(auto)으로 — 'dataMax' 로 두면 축 끝이 데이터의 최댓값 그대로라
    // 맨 위 눈금이 15.458 처럼 찍혔다(2026-10-16 신고: 0·4·8·12·15.458). auto 면 0·4·8·12·16
    result.domain = [o.min != null ? o.min : 'auto', o.max != null ? o.max : 'auto'];
  }

  // 눈금 글자 — 한 축의 소수 자릿수를 맞춘다(2026-10-16 요청: 0.5 와 0.55 가 함께면 0.50). 부동소수 꼬리(14.399999999999998)도 여기서 걷힌다.
  // tickFormatter 는 눈금 하나씩만 받으므로, recharts 가 고를 눈금을 같은 함수로 다시 구해 자릿수를 정한다(utils/chartYScale.axisTicks).
  // 쌓는 그래프는 줄마다 합이 축의 최대다 — 계열 하나의 최대로 구하면 눈금이 잘게 나와 4 가 「4.00」 이 된다
  let lo = 0; let hi = -Infinity;
  for (const row of (data || [])) {
    let pos = 0; let neg = 0;
    for (const f of fieldsOnAxis || []) {
      const v = parseFloat(row[f]);
      if (Number.isNaN(v)) continue;
      if (stacked) { if (v >= 0) pos += v; else neg += v; } else { if (v > hi) hi = v; if (v < lo) lo = v; }
    }
    if (stacked) { if (pos > hi) hi = pos; if (neg < lo) lo = neg; }
  }
  let ticks = result.ticks;
  if (!ticks) {
    const dom = result.domain;
    const fixed = Array.isArray(dom) && typeof dom[0] === 'number' && typeof dom[1] === 'number';
    const dmin = Array.isArray(dom) && typeof dom[0] === 'number' ? dom[0] : lo;
    const dmax = Array.isArray(dom) && typeof dom[1] === 'number' ? dom[1] : hi;
    ticks = axisTicks({ min: dmin, max: dmax, fixed: fixed && !!result.allowDataOverflow });
  }
  const decimals = axisDecimals(ticks);
  result.decimals = decimals;
  result.tickFormatter = (v) => formatTick(v, decimals, { thousands: !!o.thousands, unit: unit && showUnit ? unit : null });

  return result;
}

// =====================================================================
// 'job' 차트 — 막대 shape (커스텀 SVG)
//   recharts <Bar shape={...}> 가 지정한 (x,y,width,height) 안에 진행률 fill +
//   라벨 + 진행 중 빗금 패턴을 렌더.
// =====================================================================
function JobBarShape(props) {
  const { x, y, width, height, payload, isDarkMode, fontSize = 11 } = props;
  if (!payload) return null;
  const baseColor = isDarkMode ? '#374151' : '#dbeafe';
  const fillColor = '#3b82f6';
  const strokeColor = isDarkMode ? '#4b5563' : '#94a3b8';
  const textColor = isDarkMode ? '#f9fafb' : '#1f2937';
  const padY = 3;
  const innerY = y + padY;
  const innerH = Math.max(2, height - padY * 2);
  const w = Math.max(0, width || 0);
  const fillW = (payload._progress != null) ? w * (payload._progress / 100) : 0;
  const parts = [];
  if (payload._progress != null) parts.push(`${Math.round(payload._progress)}%`);
  if (payload._throughputLabel) parts.push(payload._throughputLabel);
  const label = parts.join(' · ');
  // 진행 중 작업 — fill 색 옅게 + dashed stroke 으로 시각 구분
  const ongoing = !!payload._ongoing;
  return (
    <g>
      <rect
        x={x} y={innerY} width={w} height={innerH}
        fill={baseColor}
        stroke={strokeColor}
        strokeWidth={0.5}
        strokeDasharray={ongoing ? '3 2' : undefined}
        rx={2}
        opacity={ongoing ? 0.85 : 1}
      />
      {fillW > 0 && (
        <rect
          x={x} y={innerY} width={fillW} height={innerH}
          fill={fillColor}
          rx={2}
          opacity={ongoing ? 0.7 : 1}
        />
      )}
      {label && w > 50 && (
        <text
          x={x + 6}
          y={y + height / 2 + (fontSize / 3)}
          fontSize={fontSize}
          fill={textColor}
          fontWeight="500"
          pointerEvents="none"
        >{label}</text>
      )}
    </g>
  );
}

// 'job' 차트 tooltip — 시작/종료/크기/건수/진행률/처리량 모두 표시.
function JobTooltip({ active, payload, isDarkMode }) {
  const { t } = useTranslation();
  if (!active || !payload || !payload[0]) return null;
  const d = payload[0].payload || {};
  const fmt = (v) => (v == null ? '-' : (typeof v === 'number' ? v.toLocaleString() : String(v)));
  const fmtTime = (ms) => (ms == null || isNaN(ms)) ? '-' : formatDateTime(ms);
  const bg = isDarkMode ? '#1f2937' : '#fff';
  const border = isDarkMode ? '#4b5563' : '#e5e7eb';
  const fg = isDarkMode ? '#e5e7eb' : '#1f2937';
  const muted = isDarkMode ? '#9ca3af' : '#6b7280';
  const rowStyle = { fontSize: 12, lineHeight: 1.5 };
  return (
    <div style={{
      backgroundColor: bg, border: `1px solid ${border}`, color: fg,
      padding: '8px 10px', borderRadius: 4, minWidth: 180,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{d._name}</div>
      <div style={rowStyle}><span style={{ color: muted }}>{t('monitoring:job.start')}</span> {fmtTime(d._start)}</div>
      <div style={rowStyle}>
        <span style={{ color: muted }}>{t('monitoring:job.end')}</span>{' '}
        {d._ongoing ? <em>{t('monitoring:job.running')}</em> : fmtTime(d._end)}
      </div>
      {(d._currentSize != null || d._targetSize != null) && (
        <div style={rowStyle}>
          <span style={{ color: muted }}>{t('monitoring:job.size')}</span> {fmt(d._currentSize)}
          {d._targetSize != null && <> / {fmt(d._targetSize)}</>}
        </div>
      )}
      {(d._currentCount != null || d._targetCount != null) && (
        <div style={rowStyle}>
          <span style={{ color: muted }}>{t('monitoring:job.count')}</span> {fmt(d._currentCount)}
          {d._targetCount != null && <> / {fmt(d._targetCount)}</>}
        </div>
      )}
      {d._progress != null && (
        <div style={rowStyle}>
          <span style={{ color: muted }}>{t('monitoring:job.progress')}</span> {d._progress.toFixed(1)}%
        </div>
      )}
      {d._throughputLabel && (
        <div style={rowStyle}>
          <span style={{ color: muted }}>{t('monitoring:job.rate')}</span> {d._throughputLabel}
        </div>
      )}
    </div>
  );
}

const PanelChart = ({
  panel, data, height = 260, isDarkMode = false, onElementClick,
  isFullscreen = false, hideXAxis = false, timeWindowMin = 0, displayMode = 'auto',
  // 병합(merged) 카드 안에서 sub-차트 들의 plot area 좌/우 edge 를 강제로
  // 정렬할 때 사용. MergedPanelView 가 sub-panel 들 중 max 폭을 계산해 모두에게
  // 같은 값을 prop 으로 넘김 → 모든 sub-차트의 X축 tick 이 수직 정렬됨.
  // 미지정(undefined) 이면 기존 자동 계산 (single-panel 일 때).
  forcedLeftAxisWidth, forcedRightAxisWidth,
  // 가로축 범위를 **부르는 쪽이 정한다**([시작 ms, 끝 ms]). 위아래로 겹쳐 보는 두 그래프에 같은 값을 주면 같은 x 가 같은 시각이 된다.
  // 안 주면 예전처럼 이 차트가 브라우저의 지금으로 잡는다 — 그러면 차트마다 그려진 순간이 달라 범위가 몇 초~몇 분씩 어긋나고,
  // 데이터의 시각이 DB 서버 시계일 때는 브라우저 시계와의 차이만큼 통째로 밀린다(오라클 성능 분석에서 실제로 어긋났다 2026-10-14).
  timeDomain: timeDomainProp,
  // 구간 고르기(누적 영역 그래프) — 그래프 위를 끌면 [시작 ms, 끝 ms] 를 돌려준다(락 대기 창, 2026-10-14). 고른 구간은 selectedRange 로 음영을 그린다
  onRangeSelect, selectedRange,
  // 범례 설명(2026-10-27) — (계열 키) => 짧은 설명 | null. 범례에 마우스를 올리면 툴팁으로 보인다(오라클 성능 분석의 「설명 보기」)
  legendHint,
  // 세로축 최대값(끌어서 정한 값)을 **부르는 쪽이 쥘 때**(2026-11-03) — 노드마다 그린 그래프가 같은 최대값을 쓰게(한 노드에서 끌면 다른 노드도).
  // onYMaxChange 를 주면 yMax 가 그 값이다(null = 정하지 않음). 오른쪽 보조 축은 yMaxRight / onYMaxRightChange
  yMax, onYMaxChange, yMaxRight, onYMaxRightChange,
  // 정하지 않았을 때의 공통 최대값(노드 그래프들의 데이터 최대) — 주면 이 값까지 보기 좋은 값으로 올린 범위를 쓴다
  yMaxBase, yMaxRightBase,
}) => {
  const { t } = useTranslation();
  // 계열 키는 백엔드가 준 값 그대로 쓴다 — '기타' 는 dbmsPerfRouter 의 OTHERS_KEY 로
  // 차트 계열 객체의 키이자 비교 대상이라 번역하면 차트가 깨진다.
  // 범례에 보이는 이름만 여기서 바꾼다. yFieldsConfig[f].name 이 있으면 최우선(예: 단위 스케일
  // 표시 "지표명 (×100)") — panel 이 명시적으로 넘긴 표시 이름이라 항상 우선한다.
  // '(no sql)'·'(no module)' 도 백엔드 계열 키(NVL 값) — 범례에만 뜻을 풀어 쓴다: SQL 을 실행하지 않던 활성 표본(백그라운드 프로세스·커밋·로그온 등)
  const seriesName = (f) => (panel.yFieldsConfig?.[f]?.name)
    /* eslint-disable-next-line no-restricted-syntax -- 백엔드 dbmsPerfRouter 의 OTHERS_KEY. 차트 계열 키라 번역하면 깨진다 */
    || (f === '기타' ? t('monitoring:chart.others') : f === '(no sql)' ? t('monitoring:chart.noSql') : f === '(no module)' ? t('monitoring:chart.noModule') : f);
  // 'sync' 모드만 X축 통일 로직 활성. 'auto' (default) 는 기존 동작 유지.
  const syncEnabled = displayMode === 'sync';
  // height='auto' 또는 undefined → 부모 컨테이너 100% (flex 자식). 숫자 → 고정 px.
  // 카드(전체화면 등)에 맞춰 자동 확장하려면 height='auto', 미리보기는 고정값 사용.
  const chartHeight = (height === 'auto' || height == null) ? '100%' : height;

  // 전체화면(메인 모달 fullscreen 또는 분리 popup) 시 폰트 살짝 키워 가독성 ↑.
  // recharts 의 tick / legend / label / tooltip 모두 inline style 이라 JS 로 직접 제어해야 확실.
  const tickFontSize = isFullscreen ? 13 : 11;
  const legendFontSize = isFullscreen ? 14 : 12;
  const labelFontSize = isFullscreen ? 14 : 12;
  const tooltipFontSize = isFullscreen ? 13 : 12;
  const fallbackColors = isDarkMode ? COLORS_DARK : COLORS_LIGHT;
  const gridColor = isDarkMode ? '#4b5563' : '#e5e7eb';
  const textColor = isDarkMode ? '#e5e7eb' : '#374151';

  const chartType = panel.chartType || 'line';
  const xField = panel.xField || 't';
  const yFields = panel.yFields && panel.yFields.length > 0 ? panel.yFields : ['value'];
  const yFieldsConfig = panel.yFieldsConfig || {};

  // 각 yField 의 axis 매핑
  const fieldAxis = useMemo(() => {
    const out = {};
    yFields.forEach(f => { out[f] = (yFieldsConfig[f]?.axis === 'right') ? 'right' : 'left'; });
    return out;
  }, [yFields, yFieldsConfig]);

  // 범례를 눌러 계열을 숨긴다(나.3). 지우는 것이 아니라 **감추는** 것이라
  // 범례는 그대로 두고 흐리게만 표시한다 — 사라지면 다시 켜는 버튼이 없어진다.
  // 호버 상태는 recharts 가 관리한다(useIsTooltipActive / useActiveTooltipLabel).
  // **Tooltip 자체는 달아 둔다** — 내용만 비우고 기준선은 남긴다.
  // 없애면 호버 상태가 계산되지 않아 라벨을 그릴 위치를 모른다.
  const [hiddenFields, setHiddenFields] = useState(() => new Set());
  // 흐린 계열(panel.dimmedFields, 2026-10-16) — 그리지 않고 범례에만 흐리게 남긴다. 「하나를 골라 그것만 보는」 그래프에서 나머지를 다시 고를 수 있게
  // (범례를 누르면 onElementClick 으로 그 계열을 고른다). 사용자가 범례로 감춘 것(hiddenFields)과 달리 화면이 정한다
  const dimmedKey = (panel.dimmedFields || []).join('\u0001');
  const dimmed = useMemo(() => new Set(dimmedKey ? dimmedKey.split('\u0001') : []), [dimmedKey]);
  const isOff = (f) => hiddenFields.has(f) || dimmed.has(f);
  const toggleFieldVisible = useCallback((entry) => {
    const key = entry && (entry.dataKey || entry.value);
    if (!key) return;
    setHiddenFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const leftFields = yFields.filter(f => fieldAxis[f] === 'left');
  const rightFields = yFields.filter(f => fieldAxis[f] === 'right');
  // 감춰진 계열은 라벨도 그리지 않는다.
  //
  // **의존성 키를 문자열로 만든다.** 이 배열들은 매 렌더 새로 생기는데, 그대로 useMemo 의
  // 의존성에 넣으면 아래 스케일 계산이 렌더마다 다시 돌고 차트 data 도 매번 새 배열이 된다 —
  // recharts 가 그것을 데이터 교체로 보고 다시 그리면서 개발 서버가 1.4GB 까지 부풀었다(실측).
  const leftKey = leftFields.join('');
  const rightKey = rightFields.join('');
  const hiddenKey = [...hiddenFields].sort().join('') + '|' + dimmedKey;
  const visibleLeftFields = useMemo(
    () => leftKey.split('').filter(f => f && !hiddenFields.has(f) && !dimmed.has(f)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leftKey, hiddenKey]
  );
  const visibleRightFields = useMemo(
    () => rightKey.split('').filter(f => f && !hiddenFields.has(f) && !dimmed.has(f)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rightKey, hiddenKey]
  );
  const hasRightAxis = rightFields.length > 0;

  // 축 렌더 props (line/bar 공용) — side 와 색상 전달로 좌/우 축에 맞는 라벨 회전·정렬 적용
  // 쌓는 영역 그래프는 좌축 필드가 쌓인다 — 눈금 자릿수를 구할 때 줄마다 합이 최대다
  const leftAxisProps = useMemo(() => buildAxisProps(panel.yAxis, data, leftFields, 'left', textColor, labelFontSize, chartType === 'stackedarea'), [panel.yAxis, data, leftFields, textColor, labelFontSize, chartType]);
  const rightAxisProps = useMemo(() => buildAxisProps(panel.yAxisRight, data, rightFields, 'right', textColor, labelFontSize), [panel.yAxisRight, data, rightFields, textColor, labelFontSize]);
  // sync 모드 + 시계열 + timeWindowMin 있을 때만 tick 통일 config 계산.
  const timeAxisConfig = useMemo(() => {
    if (!syncEnabled) return null;
    if (!timeWindowMin || timeWindowMin <= 0) return null;
    // isTimeSeries 는 아래에서 계산 — 여기선 일단 timeWindowMin>0 이면 계산. 추후
    // isTimeSeries=false 케이스는 tickFormatter/ticks 사용 시점에 폴백 처리.
    return deriveTimeAxisConfig(timeWindowMin);
  }, [syncEnabled, timeWindowMin]);

  // 시간축 formatter — sync 모드면 timeWindowMin 기반 통일 formatter, 아니면
  // 기존 데이터 기반 makeTimeTickFormatter (auto 모드 호환).
  // Hook 은 early return 앞에 모두 배치 (React Hook rules).
  const xTickFormatter = useMemo(() => {
    if (timeAxisConfig) {
      return (v) => {
        const ms = (typeof v === 'number') ? v : (v != null ? new Date(v).getTime() : NaN);
        if (isNaN(ms)) return String(v ?? '');
        return formatTimeTick(ms, timeAxisConfig.fmt);
      };
    }
    return makeTimeTickFormatter(data, xField);
  }, [timeAxisConfig, data, xField]);

  // 시계열 감지: data 의 첫 x 값이 Date 로 파싱 가능하면 시계열.
  // 빈 data + timeWindowMin>0 인 경우에도 시계열로 간주 (대시보드가 시간창 설정함 → 시간축).
  const isTimeSeries = useMemo(() => {
    if (Array.isArray(data) && data.length > 0) {
      const v = data[0]?.[xField];
      const ms = (typeof v === 'number') ? v : (v != null ? new Date(v).getTime() : NaN);
      return !isNaN(ms);
    }
    return timeWindowMin > 0;
  }, [data, xField, timeWindowMin]);

  // 시간 도메인 — 대시보드 상단의 "최근 N분" 설정에 따라 [now - N*60s, now] 로
  // 강제. 데이터가 sparse 해도 X축은 항상 전체 시간창 표시.
  const timeDomain = useMemo(() => {
    if (isTimeSeries && Array.isArray(timeDomainProp) && timeDomainProp.length === 2
      && Number.isFinite(timeDomainProp[0]) && Number.isFinite(timeDomainProp[1]) && timeDomainProp[1] > timeDomainProp[0]) {
      return [timeDomainProp[0], timeDomainProp[1]];
    }
    if (!isTimeSeries || !timeWindowMin || timeWindowMin <= 0) return null;
    const end = Date.now();
    const start = end - timeWindowMin * 60 * 1000;
    return [start, end];
    // 의존성: render 마다 Date.now() 갱신되도록 — 실제로는 data/timeWindowMin
    // 변경 시에만 재계산됨. 시계가 한참 흘러도 다음 fetch tick (refreshTick) 에
    // PanelTabContent 가 re-render 되면서 새 도메인 계산.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 범위는 값(시작·끝)으로 견준다. 배열 참조는 렌더마다 바뀐다
  }, [isTimeSeries, timeWindowMin, data, timeDomainProp && timeDomainProp[0], timeDomainProp && timeDomainProp[1]]);

  // sync 모드 + 시계열 + 도메인 있을 때만 명시적 ticks 배열 생성. recharts 가
  // 이 배열 위치에 정확히 tick 라벨 표시 → 여러 패널이 같은 X 위치에 동일 tick.
  // auto 모드 또는 시계열 아닌 경우 → undefined → recharts auto 계산.
  const xTicks = useMemo(() => {
    if (!syncEnabled || !isTimeSeries || !timeAxisConfig || !timeDomain) return undefined;
    return generateTimeTicks(timeDomain, timeAxisConfig.intervalMs);
  }, [syncEnabled, isTimeSeries, timeAxisConfig, timeDomain]);

  // 시계열 모드일 때 data 행에 _xMs(timestamp ms) 추가. 원본 xField 는 보존해
  // click 핸들러의 publishVar 가 ISO 문자열을 그대로 넘길 수 있게 함.
  const chartData = useMemo(() => {
    if (!isTimeSeries) return data || [];
    return (data || []).map(row => {
      const v = row[xField];
      const ms = (typeof v === 'number') ? v : (v != null ? new Date(v).getTime() : NaN);
      return { ...row, _xMs: isNaN(ms) ? null : ms };
    });
  }, [data, xField, isTimeSeries]);

  // 선 그래프의 외딴 값(양옆이 비었다) — 선분이 안 생겨 보이지 않으므로 점 하나로 그린다. 나머지 점은 그리지 않는다
  const hasValueAt = (f, i) => {
    const r = chartData[i];
    return !!r && r[f] != null && r[f] !== '' && Number.isFinite(Number(r[f]));
  };
  const isolatedDot = (f, color) => (props) => {
    const { cx, cy, index } = props || {};
    const key = `iso-${f}-${index}`;
    if (cx == null || cy == null || !hasValueAt(f, index) || hasValueAt(f, index - 1) || hasValueAt(f, index + 1)) return <g key={key} />;
    return <circle key={key} cx={cx} cy={cy} r={2.5} fill={color} stroke="none" />;
  };

  // line chart 의 X축 dataKey — 시계열 모드면 _xMs(number), 아니면 원본 xField
  const xDataKey = isTimeSeries ? '_xMs' : xField;

  // line + 시계열 + 시간창 설정 시: 데이터 비어도 X축을 표시하기 위해 early return 우회.
  // 누적 그래프의 값 뒤에 붙일 단위. 0~100 으로 고정된 축은 비율이므로 %.
  // 그 밖에는 축에 적어 둔 단위를 쓰고, 없으면 아무것도 안 붙인다(지어내지 않는다).
  const yAxisOpt = panel.yAxis || {};
  const stackValueSuffix = (yAxisOpt.min === 0 && yAxisOpt.max === 100)
    ? '%'
    : (yAxisOpt.unit ? ` ${yAxisOpt.unit}` : '');

  // ── y축 스케일 보정 (나.5) ──────────────────────────────────────────
  // 한 지표가 너무 크면 나머지가 x축 선에 붙어 한 줄로 뭉개진다. 둘을 함께 쓴다:
  //   자동 = 값이 없는 구간을 접는다(끊긴 축)  /  수동 = 사용자가 최대값을 끌어내린다.
  // **수동이 우선**이다 — 손을 댔는데 자동이 또 건드리면 무엇이 보이는지 알 수 없다.
  //
  // 0~100 처럼 축을 못박아 둔 패널(비율축)에는 걸지 않는다. 그 축은 데이터와 무관하게
  // 눈금이 고정돼야 의미가 있다.
  const [ownMax, setOwnMax] = useState(null);
  const [ownMaxR, setOwnMaxR] = useState(null);
  const userMax = typeof onYMaxChange === 'function' ? (yMax ?? null) : ownMax;
  const setUserMax = typeof onYMaxChange === 'function' ? onYMaxChange : setOwnMax;
  const userMaxR = typeof onYMaxRightChange === 'function' ? (yMaxRight ?? null) : ownMaxR;
  const setUserMaxR = typeof onYMaxRightChange === 'function' ? onYMaxRightChange : setOwnMaxR;
  const yAxisRightOpt = panel.yAxisRight || {};
  const [rangeDrag, setRangeDrag] = useState(null);   // 구간 고르기 중 { a, b } (ms)
  const justDraggedRef = useRef(false);               // 끌기로 끝난 mouseup 뒤의 click 은 고르기가 아니다
  const axisIsFixed = yAxisOpt.min != null || yAxisOpt.max != null;
  // 끌어서 최대값 정하기 — 선·누적 영역, 최대값을 못박지 않은 축(0 부터인 축은 된다 — 대기이벤트 추이가 그렇다, 2026-11-03).
  // 끊긴 축(자동)은 선 그래프에서만이고 노드 공통 최대값이 있으면 쓰지 않는다(노드마다 축 모양이 달라진다)
  const canDragY = (chartType === 'line' || chartType === 'stackedarea') && yAxisOpt.max == null;
  const canDragYR = (chartType === 'line' || chartType === 'stackedarea') && hasRightAxis && yAxisRightOpt.max == null;
  const canRescaleY = chartType === 'line' && !axisIsFixed && !(yMaxBase > 0);

  const leftValues = useMemo(() => {
    if (!(canRescaleY || (canDragY && chartType === 'line'))) return [];
    const out = [];
    (chartData || []).forEach((row) => {
      visibleLeftFields.forEach((f) => {
        // null·빈 값은 「값 없음」 이다 — Number(null) 은 0 이라 그대로 두면 빈 구간이 0 으로 그려지고 축 계산에도 0 이 끼었다(2026-10-14)
        if (row[f] == null || row[f] === '') return;
        const v = Number(row[f]);
        if (Number.isFinite(v)) out.push(v);
      });
    });
    return out;
  }, [canRescaleY, canDragY, chartType, chartData, visibleLeftFields]);
  // 누적 영역의 높이 = 줄마다 보이는 계열의 합
  const stackMax = useMemo(() => {
    if (!canDragY || chartType !== 'stackedarea') return 0;
    let m = 0;
    (chartData || []).forEach((row) => {
      let sum = 0;
      visibleLeftFields.forEach((f) => { const v = Number(row[f]); if (Number.isFinite(v) && v > 0) sum += v; });
      if (sum > m) m = sum;
    });
    return m;
  }, [canDragY, chartType, chartData, visibleLeftFields]);
  const rightMax = useMemo(() => {
    if (!canDragYR) return 0;
    let m = 0;
    (chartData || []).forEach((row) => visibleRightFields.forEach((f) => {
      if (row[f] == null || row[f] === '') return;
      const v = Number(row[f]);
      if (Number.isFinite(v) && v > m) m = v;
    }));
    return m;
  }, [canDragYR, chartData, visibleRightFields]);

  const dataMax = chartType === 'stackedarea' ? stackMax : (leftValues.length ? Math.max(...leftValues) : 0);
  // 정하지 않았을 때의 범위 — 노드 공통 최대값이 오면 그것을 보기 좋은 값으로 올려 쓴다(노드 그래프의 눈금이 같아진다)
  const baseMaxL = canDragY && yMaxBase > 0 ? niceMax(yMaxBase, 'ceil') : null;
  const baseMaxR = canDragYR && yMaxRightBase > 0 ? niceMax(yMaxRightBase, 'ceil') : null;
  const effMaxL = canDragY && userMax != null ? userMax : baseMaxL;
  const effMaxR = canDragYR && userMaxR != null ? userMaxR : baseMaxR;
  // 끌거나 공통 최대값일 때의 축 — 범위 [0, M], 눈금은 M 에 맞춘 깔끔한 간격, 자릿수는 눈금 기준(utils/chartYScale.niceTicks)
  const manualAxis = (M, opt) => {
    const ticks = niceTicks(M);
    const dec = axisDecimals(ticks);
    return { domain: [0, M], ticks, allowDataOverflow: true, tickFormatter: (v) => formatTick(v, dec, { thousands: !!opt.thousands, compact: !opt.thousands }) };
  };
  const leftManual = effMaxL != null ? manualAxis(effMaxL, yAxisOpt) : null;
  const rightManual = effMaxR != null ? manualAxis(effMaxR, yAxisRightOpt) : null;
  const axisBreak = useMemo(
    () => (canRescaleY && userMax == null ? detectAxisBreak(leftValues) : null),
    [canRescaleY, userMax, leftValues]
  );
  const breakScale = useMemo(() => (axisBreak ? makeBreakScale(axisBreak) : null), [axisBreak]);

  // 끊긴 축이면 **데이터를 표시 좌표로 옮겨** 그린다. 눈금은 fromDisplay 로 되돌려
  // 진짜 값을 적으므로, 사람이 읽는 숫자는 끝까지 원래 값이다.
  const scaledData = useMemo(() => {
    if (!breakScale) return chartData;
    return (chartData || []).map((row) => {
      const next = { ...row };
      visibleLeftFields.forEach((f) => {
        if (row[f] == null || row[f] === '') return;   // 빈 값은 빈 채로(선이 끊긴다) — 0 으로 옮기지 않는다
        const v = Number(row[f]);
        if (Number.isFinite(v)) next[f] = breakScale.toDisplay(v);
      });
      return next;
    });
  }, [breakScale, chartData, visibleLeftFields]);

  // 드래그 — y축 띠를 위아래로 끌면 최대값이 바뀐다(왼쪽 축 · 오른쪽 보조 축 각각).
  const dragRef = React.useRef(null);
  const startYDrag = (e, base, setter) => {
    if (!(base > 0)) return;
    e.preventDefault();
    dragRef.current = { y0: e.clientY, base };
    const move = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      // **아래로 끌면 축 꼭대기를 끌어내린다** = 최대값이 작아지고 작은 값이 커 보인다.
      // 부호를 반대로 뒀더니 끌수록 더 눌려서, 고치려던 문제가 심해졌다(실측).
      // 200px 당 10배. 값은 보기 좋은 값으로 맞춘다 — 그대로 두면 눈금의 소수 자릿수가 끝없이 늘었다(2026-11-03)
      const factor = Math.pow(10, -(ev.clientY - d.y0) / 200);
      setter(niceMax(Math.max(d.base * 0.02, d.base * factor), 'round'));
    };
    // 누른 자리의 문서에 건다(utils/dragListen) — 전역 window 는 본창의 것이라 「새 창으로 분리」 한 그래프에서는 끌기가 안 됐다
    listenDrag(e, move, () => { dragRef.current = null; });
  };
  const onAxisDragStart = (e) => { if (canDragY) startYDrag(e, effMaxL != null ? effMaxL : dataMax, setUserMax); };
  const onAxisDragStartR = (e) => { if (canDragYR) startYDrag(e, effMaxR != null ? effMaxR : rightMax, setUserMaxR); };
  const resetUserMax = () => { if (userMax != null) setUserMax(null); if (userMaxR != null) setUserMaxR(null); };

  const showEmptyMsgEarly = (!data || data.length === 0)
    && !(chartType === 'line' && isTimeSeries && timeDomain);

  if (showEmptyMsgEarly) {
    return <div style={{ height: chartHeight, flex: chartHeight === '100%' ? 1 : undefined, minHeight: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>{t('monitoring:chart.noData')}</div>;
  }

  const tooltipStyle = {
    backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
    border: `1px solid ${gridColor}`,
    color: textColor,
    fontSize: tooltipFontSize,
  };
  // 값 라벨의 테두리 색 — 배경 박스 대신 글자를 이 색으로 깔아 가독성만 낸다
  const chartBgColor = isDarkMode ? '#1f2937' : '#ffffff';
  // 박스는 안 그리지만 **기준선은 남긴다** — 어느 시각의 값인지 보여야 한다
  const hoverCursor = { stroke: isDarkMode ? '#9ca3af' : '#6b7280', strokeDasharray: '3 3' };


  // yField 의 색상 — yFieldsConfig 우선, 없으면 fallback 팔레트
  const colorOf = (field, idx) => yFieldsConfig[field]?.color || fallbackColors[idx % fallbackColors.length];

  // 범례 정렬 — recharts <Legend align={}> 는 'left'|'center'|'right'
  const legendAlign = panel.legendAlign === 'left' || panel.legendAlign === 'right' ? panel.legendAlign : 'center';
  const legendVerticalAlign = panel.legendPosition === 'top' ? 'top' : 'bottom';

  // 차트 클릭 시 datum 추출 → 부모로 전달.
  // line: chart-level onClick. activePayload(가까운 데이터 포인트) 가 비어있어도
  //       activeLabel(커서 X 위치의 데이터 라벨) 로 fallback 해서 X 값 확보.
  //       dot={false} 인 line 에선 클릭 위치에 따라 activePayload 가 비어있는
  //       경우가 있으므로 양쪽을 모두 고려.
  // bar:  개별 Bar 의 onClick 으로 정확한 시리즈/datum 획득.
  const handleLineChartClick = (state) => {
    if (!onElementClick || !state || justDraggedRef.current) return;
    const p0 = state.activePayload?.[0];
    // x 값 우선순위: activePayload 의 데이터 포인트 → activeLabel(축의 X 값).
    // 단 시계열 모드에서는 activeLabel 이 _xMs(숫자) 라 publishVar 가 ms 를
    // 그대로 넘기게 됨 — consumer SQL 이 ISO 를 기대하면 깨지므로 시계열 모드에선
    // 데이터 포인트가 있을 때만 발행 (fallback skip).
    let x = p0?.payload?.[xField];
    if ((x === undefined || x === null) && !isTimeSeries) x = state.activeLabel;
    if (x === undefined || x === null) return;
    onElementClick({
      x,
      y: p0?.value,
      series: p0?.dataKey ?? (yFields.length > 0 ? yFields[0] : undefined),
      payload: p0?.payload,
    });
  };
  const handleBarClick = (field) => (barProps) => {
    if (!onElementClick) return;
    const payload = barProps?.payload || {};
    onElementClick({
      x: payload[xField],
      y: payload[field],
      series: field,
      payload,
    });
  };
  // stackedarea: 쌓인 영역 하나(Area)를 직접 클릭 — bar 와 동일하게 field 별로 정확한 계열 획득.
  const handleAreaClick = (field) => (areaProps) => {
    if (!onElementClick || justDraggedRef.current) return;
    const payload = areaProps?.payload || {};
    onElementClick({
      x: payload[xField],
      y: payload[field],
      series: field,
      payload,
    });
  };
  // 범례 클릭 — 특정 데이터 포인트가 아니라 계열 자체를 고른 것이라 x/y 는 없다.
  const handleLegendClick = (entry) => {
    if (!onElementClick || !entry) return;
    onElementClick({ x: undefined, y: undefined, series: entry.dataKey, payload: null });
  };

  const cursorStyle = onElementClick ? { cursor: 'pointer' } : undefined;

  // 레이아웃 보정 — label 있으면 tick numbers + 회전된 라벨이 함께 들어갈 폭 확보.
  // dx 로 라벨을 차트 라인 가까이 끌어왔으므로 폭을 너무 키울 필요 없음.
  // forced* prop 이 있으면 (병합 카드 컨텍스트) 그 값으로 강제 — sub-차트들 정렬.
  // 축 이름은 위쪽 여백에 가로로 둔다 — 축 폭을 더 잡을 까닭이 없다. 대신 위 여백을 늘린다(chartTop)
  const localLeftWidth  = 45;
  const localRightWidth = 45;
  const chartTop = (leftAxisProps.topLabel || rightAxisProps.topLabel) ? 26 : 8;
  const leftWidth  = (typeof forcedLeftAxisWidth  === 'number' && forcedLeftAxisWidth  > 0)
    ? forcedLeftAxisWidth  : localLeftWidth;
  const rightWidth = (typeof forcedRightAxisWidth === 'number' && forcedRightAxisWidth > 0)
    ? forcedRightAxisWidth : localRightWidth;
  // 자기 패널은 우측 축 미사용이지만 병합 카드의 다른 sub-panel 이 우측 축을
  // 가지고 있어 정렬을 위해 우측 공간을 reserve 해야 하는 경우 — chart margin.right
  // 에 반영. (recharts 가 우측 YAxis 없으면 그 공간을 plot area 가 차지하므로)
  const reservedRightForAlign = (!hasRightAxis
      && typeof forcedRightAxisWidth === 'number' && forcedRightAxisWidth > 0)
    ? forcedRightAxisWidth : 0;

  // 축 끌기 손잡이 — 왼쪽 축 · 오른쪽 보조 축(2026-11-03 — 예전에는 왼쪽만 됐다). 두 칸 위에 투명한 띠를 얹는다
  const yHandles = (
    <>
      {canDragY && (
        <div
          className="panelchart-ydrag"
          style={{ width: leftWidth }}
          onMouseDown={onAxisDragStart}
          onDoubleClick={() => setUserMax(null)}
          title={t('monitoring:chart.yDragHint')}
          role="presentation"
        />
      )}
      {canDragYR && (
        <div
          className="panelchart-ydrag panelchart-ydrag-right"
          style={{ width: rightWidth, right: 16 }}
          onMouseDown={onAxisDragStartR}
          onDoubleClick={() => setUserMaxR(null)}
          title={t('monitoring:chart.yDragHint')}
          role="presentation"
        />
      )}
      {((canDragY && userMax != null) || (canDragYR && userMaxR != null)) && (
        // 손을 댔다는 사실은 **보여야 한다** — 축이 데이터 범위와 다른데 말이 없으면
        // 다음에 열었을 때 값이 잘린 줄 모른다.
        <button
          type="button"
          className="panelchart-yreset"
          onClick={resetUserMax}
          title={t('monitoring:chart.yResetHint')}
        >
          {t('monitoring:chart.yReset')}
        </button>
      )}
    </>
  );

  // 끌어서 구간 고르기 — 누적 영역·선 그래프, 시계열일 때만(가로축이 ms). 같은 점에서 놓으면(그냥 누름) 고르지 않는다.
  // 끌고 난 직후의 click 은 버린다 — 영역·선을 누르면 계열을 고르는 그래프(오라클 성능 분석)에서 끌기가 고르기로도 읽혔다
  const canSelect = !!onRangeSelect && isTimeSeries;
  // 끈 위치의 시각 — 가로축 범위(timeDomain)가 있으면 **마우스 위치**로 계산한다. 가장 가까운 데이터 점(activeLabel)으로 읽으면 선이 없는 곳
  // (실시간 메트릭은 약 1시간치뿐이라 전체 기간의 앞쪽이 비어 있다)을 끌 때 시작·끝이 같은 점이 되어 고르기가 안 됐다(신고 2026-10-16)
  const dragX = (e, ev) => {
    // recharts 3 은 첫 인자에 마우스 좌표를 주지 않는다 — 두 번째 인자(이벤트)의 clientX 와 그래프 틀(currentTarget)로 계산한다.
    // 화면 px → svg 좌표는 틀의 실제 폭 대비로 바꾼다(웹의 CSS zoom 아래에서도 맞다)
    const wrap = ev && ev.currentTarget && ev.currentTarget.querySelector ? ev.currentTarget : null;
    // 그래프 본체는 틀의 **바로 아래** svg — 범례 아이콘도 같은 이름(recharts-surface)의 작은 svg 라 그것이 먼저 잡혔다
    const svg = wrap && wrap.querySelector(':scope > svg.recharts-surface');
    const plotRect = wrap && wrap.querySelector('clipPath rect');
    if (Array.isArray(timeDomain) && svg && plotRect && typeof ev.clientX === 'number') {
      const box = svg.getBoundingClientRect();
      const svgW = Number(svg.getAttribute('width')) || box.width;
      const sx = box.width > 0 ? ((ev.clientX - box.left) / box.width) * svgW : null;
      const px = Number(plotRect.getAttribute('x')); const pw = Number(plotRect.getAttribute('width'));
      if (sx != null && pw > 0) {
        const ratio = Math.min(1, Math.max(0, (sx - px) / pw));
        return timeDomain[0] + ratio * (timeDomain[1] - timeDomain[0]);
      }
    }
    return e && typeof e.activeLabel === 'number' ? e.activeLabel : (e && e.activeLabel != null ? Number(e.activeLabel) : null);
  };
  const selectHandlers = canSelect ? {
    onMouseDown: (e, ev) => { const x = dragX(e, ev); if (x != null && Number.isFinite(x)) setRangeDrag({ a: x, b: x }); },
    onMouseMove: (e, ev) => { if (!rangeDrag) return; const x = dragX(e, ev); if (x != null && Number.isFinite(x)) setRangeDrag(d => (d ? { ...d, b: x } : d)); },
    onMouseUp: () => {
      if (rangeDrag && rangeDrag.a !== rangeDrag.b) {
        justDraggedRef.current = true;
        setTimeout(() => { justDraggedRef.current = false; }, 0);
        onRangeSelect(Math.min(rangeDrag.a, rangeDrag.b), Math.max(rangeDrag.a, rangeDrag.b));
      }
      setRangeDrag(null);
    },
    onMouseLeave: () => setRangeDrag(null),
  } : {};
  // 고른 구간의 음영(락 대기 — 그래프는 그대로 두고 트리만 그 구간으로 좁힌다)
  const selArea = canSelect && selectedRange && selectedRange[0] != null ? (
    <ReferenceArea yAxisId="left" x1={selectedRange[0]} x2={selectedRange[1]} fill="#3b82f6" fillOpacity={0.12} stroke="#3b82f6" strokeOpacity={0.6} ifOverflow="hidden" />
  ) : null;
  const dragArea = canSelect && rangeDrag && rangeDrag.a !== rangeDrag.b ? (
    <ReferenceArea yAxisId="left" x1={Math.min(rangeDrag.a, rangeDrag.b)} x2={Math.max(rangeDrag.a, rangeDrag.b)} fill="#3b82f6" fillOpacity={0.2} ifOverflow="hidden" />
  ) : null;

  if (chartType === 'line') {
    // 끊긴 축이면 표시 좌표로 옮긴 데이터를, 아니면 원본을 그린다.
    const lineData = breakScale ? scaledData : chartData;
    // 끊긴 축의 눈금은 표시 좌표다 — 원래 값으로 되돌린 눈금들로 자릿수를 맞춘다
    const breakDecimals = breakScale ? axisDecimals((breakScale.ticks || []).map(d => breakScale.fromDisplay(d))) : 0;
    // 축 속성은 세 갈래다: 끊긴 축 / 사용자가 정한 최대값 / 기본(패널 설정 그대로).
    const yProps = breakScale
      ? {
        domain: breakScale.domain,
        ticks: breakScale.ticks,
        allowDataOverflow: true,
        tickFormatter: (d) => formatTick(breakScale.fromDisplay(d), breakDecimals, { thousands: !!yAxisOpt.thousands, compact: !yAxisOpt.thousands }),
      }
      : (leftManual
        // 사람이 정한(또는 노드 공통) 최대값 — 잘라 낸 위쪽은 잘린 채로 둔다
        ? leftManual
        : {
          domain: leftAxisProps.domain,
          ticks: leftAxisProps.ticks,
          allowDataOverflow: leftAxisProps.allowDataOverflow,
          tickFormatter: leftAxisProps.tickFormatter,
        });

    return (
      // 손잡이를 축 위에 얹으려면 기준 컨테이너가 필요하다.
      // ResponsiveContainer 는 height 를 부모에서 받으므로 래퍼도 같은 높이를 갖는다.
      <div className="panelchart-ywrap" style={{ position: 'relative', height: chartHeight, width: '100%' }}>
        {yHandles}
      <ResponsiveContainer width="100%" height={chartHeight}>
        <LineChart
          data={lineData}
          margin={{ top: chartTop, right: 16 + reservedRightForAlign, left: 0, bottom: 4 }}
          onClick={handleLineChartClick}
          style={canSelect ? { ...(cursorStyle || {}), cursor: 'crosshair', userSelect: 'none' } : cursorStyle}
          syncId={syncEnabled && isTimeSeries ? 'monitoring-timesync' : undefined}
          {...selectHandlers}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            dataKey={xDataKey}
            type={isTimeSeries ? 'number' : 'category'}
            scale={isTimeSeries ? 'time' : undefined}
            /* timeDomain 없고 시계열일 땐 ['dataMin','dataMax'] 명시 — recharts 의 number-type
               default ([0, dataMax]) 가 timestamp 값을 우측 edge 에 압축시키는 문제 회피.
               (preview 같이 timeWindowMin=0 인 경우에 line 차트가 한 점에 몰려 보임) */
            domain={timeDomain || (isTimeSeries ? ['dataMin', 'dataMax'] : undefined)}
            allowDataOverflow={!!timeDomain}
            tick={{ fill: textColor, fontSize: tickFontSize }}
            tickFormatter={xTickFormatter}
            ticks={xTicks}
            minTickGap={xTicks ? 1 : 40}
            hide={hideXAxis}
          />
          <YAxis
            yAxisId="left"
            tick={{ fill: textColor, fontSize: tickFontSize }}
            width={leftWidth}
            label={leftAxisProps.label}
            ticks={yProps.ticks}
            domain={yProps.domain}
            allowDataOverflow={yProps.allowDataOverflow}
            tickFormatter={yProps.tickFormatter}
          />
          {hasRightAxis && (
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: textColor, fontSize: tickFontSize }}
              width={rightWidth}
              label={rightAxisProps.label}
              ticks={(rightManual || rightAxisProps).ticks}
              domain={(rightManual || rightAxisProps).domain}
              allowDataOverflow={(rightManual || rightAxisProps).allowDataOverflow}
              tickFormatter={(rightManual || rightAxisProps).tickFormatter}
            />
          )}
          {/* 누적 그래프와 같은 규칙 — 박스는 안 그리고 값은 선 옆에 직접 쓴다 */}
          <Tooltip content={renderNoTooltip} cursor={hoverCursor} />
          <Legend
            wrapperStyle={{ fontSize: legendFontSize, cursor: 'pointer' }}
            verticalAlign={legendVerticalAlign}
            align={legendAlign}
            // 계열을 누르면 고르는 그래프면 범례도 고르기다(영역 그래프와 같게) — 아니면 예전처럼 감추기/보이기
            onClick={onElementClick ? handleLegendClick : toggleFieldVisible}
            formatter={(value, entry) => {
              const key = entry && (entry.dataKey || value);
              const off = hiddenFields.has(key);
              const dim = dimmed.has(key);
              return <span title={legendHint ? (legendHint(key) || undefined) : undefined} style={{ opacity: off || dim ? 0.4 : 1, textDecoration: off ? 'line-through' : 'none' }}>{value}</span>;
            }}
          />
          {yFields.map((f, idx) => (
            <Line
              key={f}
              yAxisId={fieldAxis[f]}
              type="monotone"
              dataKey={f}
              stroke={colorOf(f, idx)}
              strokeWidth={2}
              // 계열 설정의 dashed — 보조 축의 다른 뜻의 선(대기 수 등)을 쌓는 그래프의 오른쪽 선과 같은 모양으로
              strokeDasharray={panel.yFieldsConfig?.[f]?.dashed ? '5 3' : undefined}
              // 값이 비는 구간은 잇지 않는다(평균 = 합 ÷ 횟수 — 그 구간에 대기가 없으면 평균이 없다, 0 으로 메우면 거짓말이다).
              // 대신 **양옆이 빈 외딴 값**은 선분이 생기지 않아 사라지므로 점으로 그린다(2026-10-18 「끊겨 보인다」 신고)
              dot={isolatedDot(f, colorOf(f, idx))}
              name={seriesName(f)}
              isAnimationActive={false}
              hide={isOff(f)}
            />
          ))}
          {selArea}
          {dragArea}
          {breakScale && (
            <AxisBreakMark
              displayValue={breakScale.breakAt}
              color={textColor}
              bgColor={chartBgColor}
              fontSize={tickFontSize}
            />
          )}
          {/* 값 라벨은 **맨 마지막에** 그린다 — SVG 는 나중 것이 위다. 끊긴 축 표시(두 줄)를 뒤에 그려서 라벨이 가려졌다(2026-10-14 신고) */}
          <HoverValueLabels
            mode="line"
            data={lineData}
            inverse={breakScale ? breakScale.fromDisplay : null}
            xDataKey={xDataKey}
            fields={[...visibleLeftFields, ...visibleRightFields]}
            yFields={yFields}
            fieldAxis={fieldAxis}
            seriesName={seriesName}
            colorOf={colorOf}
            bgColor={chartBgColor}
            fontSize={tickFontSize}
          />
        </LineChart>
      </ResponsiveContainer>
      </div>
    );
  }

  // 누적 영역 + (선택) 우축 라인 —
  //   좌축 필드는 stackId 를 공유해 쌓이고, 우축 필드는 라인으로 겹쳐 그린다.
  //   Bar 가 아니라 Area 인 이유: 이 차트의 X 축은 type='number' + scale='time' 인데
  //   recharts 는 숫자 축에서 막대 폭을 추론하지 못해 1px 로 뭉갠다. Area 는 그 제약이 없다.
  if (chartType === 'stackedarea') {
    return (
      <div className="panelchart-ywrap" style={{ position: 'relative', height: chartHeight, width: '100%' }}>
        {yHandles}
      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart
          data={chartData}
          margin={{ top: chartTop, right: 16 + reservedRightForAlign, left: 0, bottom: 4 }}
          style={canSelect ? { ...(cursorStyle || {}), cursor: 'crosshair', userSelect: 'none' } : cursorStyle}
          syncId={syncEnabled && isTimeSeries ? 'monitoring-timesync' : undefined}
          {...selectHandlers}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            dataKey={xDataKey}
            type={isTimeSeries ? 'number' : 'category'}
            scale={isTimeSeries ? 'time' : undefined}
            domain={timeDomain || (isTimeSeries ? ['dataMin', 'dataMax'] : undefined)}
            allowDataOverflow={!!timeDomain}
            tick={{ fill: textColor, fontSize: tickFontSize }}
            tickFormatter={xTickFormatter}
            ticks={xTicks}
            minTickGap={xTicks ? 1 : 40}
            hide={hideXAxis}
          />
          <YAxis
            yAxisId="left"
            tick={{ fill: textColor, fontSize: tickFontSize }}
            width={leftWidth}
            label={leftAxisProps.label}
            ticks={(leftManual || leftAxisProps).ticks}
            domain={(leftManual || leftAxisProps).domain}
            allowDataOverflow={(leftManual || leftAxisProps).allowDataOverflow}
            tickFormatter={(leftManual || leftAxisProps).tickFormatter}
          />
          {hasRightAxis && (
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: textColor, fontSize: tickFontSize }}
              width={rightWidth}
              label={rightAxisProps.label}
              ticks={(rightManual || rightAxisProps).ticks}
              domain={(rightManual || rightAxisProps).domain}
              allowDataOverflow={(rightManual || rightAxisProps).allowDataOverflow}
              tickFormatter={(rightManual || rightAxisProps).tickFormatter}
            />
          )}
          {/* 박스는 그리지 않는다(그래프를 가린다는 신고). 값은 아래 Customized 가
              각 영역 안에 직접 그린다. Tooltip 자체를 없애면 activeTooltipIndex 가
              안 나와 그 라벨을 그릴 위치를 모른다. */}
          <Tooltip content={renderNoTooltip} cursor={hoverCursor} />
          <Legend
            wrapperStyle={{ fontSize: legendFontSize, cursor: onElementClick ? 'pointer' : undefined }}
            verticalAlign={legendVerticalAlign}
            align={legendAlign}
            onClick={onElementClick ? handleLegendClick : undefined}
            formatter={(value, entry) => {
              const key = entry && (entry.dataKey || value);
              return <span title={legendHint ? (legendHint(key) || undefined) : undefined} style={{ opacity: dimmed.has(key) ? 0.4 : 1 }}>{value}</span>;
            }}
          />
          {leftFields.map((f) => (
            <Area
              key={f}
              hide={dimmed.has(f)}
              yAxisId="left"
              type="monotone"
              dataKey={f}
              stackId="stack"
              stroke={colorOf(f, yFields.indexOf(f))}
              fill={colorOf(f, yFields.indexOf(f))}
              fillOpacity={0.72}
              name={seriesName(f)}
              isAnimationActive={false}
              onClick={onElementClick ? handleAreaClick(f) : undefined}
              legendType="square"
              // 마우스 위치의 점은 그리지 않는다 — 축 범위(0~100)로 잘린 그림 영역 안에 그려져 100% 에서 반쪽만 보이고, 0 인 계열은 바닥에 점이 남았다(2026-10-14 신고).
              // 점은 값 라벨 층(HoverValueLabels — 잘리지 않는다)이 0 보다 큰 띠의 위끝에만 그린다
              activeDot={false}
              style={cursorStyle}
            />
          ))}
          {rightFields.map((f) => (
            <Line
              key={f}
              yAxisId="right"
              type="monotone"
              dataKey={f}
              stroke={colorOf(f, yFields.indexOf(f))}
              strokeWidth={2}
              strokeDasharray="5 3"
              dot={false}
              name={seriesName(f)}
              isAnimationActive={false}
            />
          ))}
          {selArea}
          {dragArea}
          <HoverValueLabels
            mode="stack"
            data={chartData}
            xDataKey={xDataKey}
            fields={visibleLeftFields}
            yFields={yFields}
            fieldAxis={fieldAxis}
            seriesName={seriesName}
            colorOf={colorOf}
            bgColor={chartBgColor}
            fontSize={tickFontSize}
            valueSuffix={stackValueSuffix}
          />
        </ComposedChart>
      </ResponsiveContainer>
      </div>
    );
  }

  if (chartType === 'vbar') {
    return (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={data}
          margin={{ top: chartTop, right: 16, left: 0, bottom: 40 }}
          style={cursorStyle}
          syncId={syncEnabled ? 'monitoring-timesync' : undefined}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis dataKey={xField} tick={{ fill: textColor, fontSize: tickFontSize }} angle={-30} textAnchor="end" height={70} interval={0} hide={hideXAxis} />
          <YAxis
            yAxisId="left"
            tick={{ fill: textColor, fontSize: tickFontSize }}
            width={Math.max(55, leftWidth)}
            label={leftAxisProps.label}
            ticks={leftAxisProps.ticks}
            domain={leftAxisProps.domain}
            allowDataOverflow={leftAxisProps.allowDataOverflow}
            tickFormatter={leftAxisProps.tickFormatter}
          />
          {hasRightAxis && (
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: textColor, fontSize: tickFontSize }}
              width={Math.max(55, rightWidth)}
              label={rightAxisProps.label}
              ticks={rightAxisProps.ticks}
              domain={rightAxisProps.domain}
              allowDataOverflow={rightAxisProps.allowDataOverflow}
              tickFormatter={rightAxisProps.tickFormatter}
            />
          )}
          <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: textColor }} />
          <Legend wrapperStyle={{ fontSize: legendFontSize }} verticalAlign={legendVerticalAlign} align={legendAlign} />
          {yFields.map((f, idx) => (
            <Bar
              key={f}
              yAxisId={fieldAxis[f]}
              dataKey={f}
              fill={colorOf(f, idx)}
              name={seriesName(f)}
              isAnimationActive={false}
              onClick={handleBarClick(f)}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === 'hbar') {
    // HBar 는 X 축이 값축, Y 축이 카테고리 — 보조 축 개념 적용 어려워 단일 축으로 한정.
    return (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart data={data} layout="vertical" margin={{ top: chartTop, right: 16, left: 8, bottom: 4 }} style={cursorStyle}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            type="number"
            tick={{ fill: textColor, fontSize: tickFontSize }}
            label={leftAxisProps.label}
            ticks={leftAxisProps.ticks}
            domain={leftAxisProps.domain}
            allowDataOverflow={leftAxisProps.allowDataOverflow}
            tickFormatter={leftAxisProps.tickFormatter}
          />
          <YAxis type="category" dataKey={xField} tick={{ fill: textColor, fontSize: tickFontSize }} width={130} />
          <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: textColor }} />
          <Legend wrapperStyle={{ fontSize: legendFontSize }} verticalAlign={legendVerticalAlign} align={legendAlign} />
          {yFields.map((f, idx) => (
            <Bar
              key={f}
              dataKey={f}
              fill={colorOf(f, idx)}
              name={seriesName(f)}
              isAnimationActive={false}
              onClick={handleBarClick(f)}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === 'grid') {
    // 그리드(테이블) — SQL 결과 행을 그대로 표시.
    // 컬럼 우선순위: [xField, ...yFields] 명시값 → 없으면 첫 행의 모든 키.
    // 행 클릭 시 onElementClick 호출 (publishVar 의 x 추출은 xField 컬럼 값 사용).
    const explicit = [xField, ...yFields].filter(c => c && c !== 'value' && c !== 'series_1' && c !== 'x');
    const cols = explicit.length > 0 ? explicit : Object.keys(data[0] || {});
    const formatCell = (v) => {
      if (v === null || v === undefined) return '-';
      if (typeof v === 'number') return Number.isInteger(v) ? v.toLocaleString() : v.toFixed(2);
      if (v instanceof Date) return formatDateTime(v);
      // ISO 문자열 자동 변환 시도
      if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
        const d = new Date(v);
        if (!isNaN(d.getTime())) return formatDateTime(d);
      }
      return String(v);
    };
    const handleRowClick = (row) => {
      if (!onElementClick) return;
      onElementClick({
        x: row[xField],
        y: yFields.length > 0 ? row[yFields[0]] : undefined,
        series: yFields.length > 0 ? yFields[0] : undefined,
        payload: row,
      });
    };
    const cellPad = '4px 8px';
    return (
      <div style={{ height: chartHeight, flex: chartHeight === '100%' ? 1 : undefined, minHeight: 0, overflow: 'auto', border: `1px solid ${gridColor}`, borderRadius: 4 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: tooltipFontSize, color: textColor }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: isDarkMode ? '#1f2937' : '#f9fafb' }}>
            <tr>
              {cols.map(c => (
                <th key={c} style={{
                  padding: cellPad,
                  textAlign: 'left',
                  fontWeight: 600,
                  borderBottom: `1px solid ${gridColor}`,
                  whiteSpace: 'nowrap',
                  color: textColor,
                }}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr
                key={idx}
                onClick={() => handleRowClick(row)}
                style={{
                  cursor: onElementClick ? 'pointer' : 'default',
                  background: idx % 2 === 0 ? 'transparent' : (isDarkMode ? 'rgba(255,255,255,0.02)' : '#fafafa'),
                }}
              >
                {cols.map(c => {
                  const val = row[c];
                  const isNum = typeof val === 'number';
                  return (
                    <td key={c} style={{
                      padding: cellPad,
                      borderBottom: `1px solid ${gridColor}`,
                      textAlign: isNum ? 'right' : 'left',
                      whiteSpace: 'nowrap',
                      fontVariantNumeric: isNum ? 'tabular-nums' : undefined,
                    }}>{formatCell(val)}</td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (chartType === 'job') {
    // 작업 (job) — 시계열 Gantt-like.
    // panel.jobFields = { name, startTime, endTime, targetSize, currentSize,
    //                     targetCount, currentCount } (name/startTime 필수)
    const jf = panel.jobFields || {};
    if (!jf.name || !jf.startTime) {
      return <div style={{ height: chartHeight, flex: chartHeight === '100%' ? 1 : undefined, padding: 12, color: '#9ca3af', fontSize: 12 }}>
        {t('monitoring:chart.jobFieldsMissing')}
      </div>;
    }
    const jobData = (data || []).map((row, idx) => {
      const startMs = jobParseTime(row[jf.startTime]);
      const rawEnd  = jf.endTime ? row[jf.endTime] : null;
      const isOngoing = (rawEnd == null || rawEnd === '');
      const endMs = isOngoing ? Date.now() : jobParseTime(rawEnd);
      const targetSize  = jf.targetSize  ? jobNumOrNull(row[jf.targetSize])  : null;
      const currentSize = jf.currentSize ? jobNumOrNull(row[jf.currentSize]) : null;
      const targetCount  = jf.targetCount  ? jobNumOrNull(row[jf.targetCount])  : null;
      const currentCount = jf.currentCount ? jobNumOrNull(row[jf.currentCount]) : null;
      const elapsedSec = (isFinite(startMs) && isFinite(endMs))
        ? Math.max(0, (endMs - startMs) / 1000) : 0;
      const progress = (targetSize != null && currentSize != null && targetSize > 0)
        ? Math.min(100, (currentSize / targetSize) * 100) : null;
      const throughput = (currentSize != null && elapsedSec > 0)
        ? currentSize / elapsedSec : null;
      return {
        ...row,
        _name: String(row[jf.name] ?? '(no name)'),
        _start: startMs,
        _end: endMs,
        _range: [startMs, endMs],
        _ongoing: isOngoing,
        _progress: progress,
        _throughput: throughput,
        _throughputLabel: jobFormatRate(throughput),
        _targetSize: targetSize, _currentSize: currentSize,
        _targetCount: targetCount, _currentCount: currentCount,
        _idx: idx,
      };
    }).filter(r => isFinite(r._start) && isFinite(r._end));

    if (jobData.length === 0) {
      return <div style={{ height: chartHeight, flex: chartHeight === '100%' ? 1 : undefined, minHeight: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>{t('monitoring:chart.noData')}</div>;
    }

    // Y축 폭 — 작업명 길이에 따라 가변 (가장 긴 이름 ~기반). 단순 추정.
    const maxNameLen = jobData.reduce((m, d) => Math.max(m, (d._name || '').length), 0);
    const yAxisWidth = Math.min(220, Math.max(100, maxNameLen * 7 + 16));

    return (
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={jobData}
          layout="vertical"
          margin={{ top: chartTop, right: 16, left: 8, bottom: 4 }}
          syncId={syncEnabled ? 'monitoring-timesync' : undefined}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            type="number"
            scale="time"
            domain={timeDomain || ['dataMin', 'dataMax']}
            tickFormatter={xTickFormatter}
            ticks={xTicks}
            tick={{ fill: textColor, fontSize: tickFontSize }}
            minTickGap={xTicks ? 1 : 40}
            allowDataOverflow={!!timeDomain}
            hide={hideXAxis}
          />
          <YAxis
            type="category"
            dataKey="_name"
            width={yAxisWidth}
            tick={{ fill: textColor, fontSize: tickFontSize }}
            interval={0}
          />
          <Tooltip content={<JobTooltip isDarkMode={isDarkMode} />} cursor={{ fill: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }} />
          <Bar
            dataKey="_range"
            isAnimationActive={false}
            shape={(props) => <JobBarShape {...props} isDarkMode={isDarkMode} fontSize={tickFontSize} />}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  return <div style={{ color: '#ef4444', fontSize: 12 }}>{t('monitoring:chart.unsupportedType', { type: chartType })}</div>;
};

export default PanelChart;
